/**
 * Plan & budget — the plan as a pool, and each template's share of it.
 *
 * Three questions, top to bottom:
 *
 *   1. How much of the plan have I promised away?  The pool: plan fee, what
 *      is budgeted, what is left — one bar with every budget laid along it.
 *   2. Have I promised more than the plan pays for?  If so, by how much, what
 *      that excess actually buys (overage pages), and three ways out: scale
 *      everything to fit, compare plans, or accept it as planned overage.
 *   3. Which template is going to blow its budget?  Every template with this
 *      cycle's measured spend, its projection to the cycle end, and its
 *      budget — worst first.
 *
 * Spend is pages at the plan rate (fee ÷ included pages), measured per job for
 * the current billing cycle, so budgets that add up to the fee add up to the
 * allowance. See utils/budgets.js.
 */
import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  AlertCircle, ArrowUpRight, Check, Download, Layers, PiggyBank, Plus, Search, Undo2, Wand2, X,
} from 'lucide-react'
import { Card, CountUp, EmptyState, HelpTip, Segmented, usePrefersReducedMotion } from './BillingPrimitives'
import BudgetAllocationBar from './BudgetAllocationBar'
import BudgetEditor from './BudgetEditor'
import { useBudgets } from './useBudgets'
import { downloadCsv, formatCycleLabel, formatInt, formatMoneyAs, formatMoneyPreciseAs } from '../../utils/billing'
import { encodeRows } from '../../utils/billingReports'
import {
  FILTERS, SORTS, STATUS, budgetPool, buildBudgetCsvRows, buildBudgetRows, segmentColor, summariseAllocation, untrackedSpend,
} from '../../utils/budgets'

const STATUS_STYLE = {
  unset: { chip: 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400', dot: 'bg-gray-300 dark:bg-gray-600', bar: 'bg-gray-300 dark:bg-gray-600' },
  ok: { chip: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400', dot: 'bg-emerald-500', bar: 'bg-emerald-500 dark:bg-emerald-400' },
  watch: { chip: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400', dot: 'bg-amber-500', bar: 'bg-amber-500 dark:bg-amber-400' },
  risk: { chip: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400', dot: 'bg-orange-500', bar: 'bg-orange-500 dark:bg-orange-400' },
  over: { chip: 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400', dot: 'bg-rose-500', bar: 'bg-rose-500 dark:bg-rose-400' },
}

const SORT_OPTIONS = [
  { value: 'status', label: 'Needs attention first' },
  { value: 'spent', label: 'Highest spend' },
  { value: 'budget', label: 'Largest budget' },
  { value: 'name', label: 'Name' },
]

export default function PlanBudgetPanel({
  plan, data, orgKey, chart, onCompare, onDone, onWarn,
}) {
  const pool = useMemo(() => budgetPool(plan), [plan])
  const budgetsApi = useBudgets(orgKey)
  const { budgets, overPlanAcknowledged } = budgetsApi

  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState('all')
  const [sort, setSort] = useState('status')
  const [editing, setEditing] = useState(null)

  const nativeCode = pool?.currency?.code || 'GBP'
  // Budgets are plan money: always in the plan's currency, never converted.
  const displayCode = nativeCode
  const native = pool?.currency || { code: 'GBP', symbol: '£' }
  const format = (v, opts = { pence: false }) => formatMoneyAs(v, native, displayCode, opts)
  const formatPrecise = v => formatMoneyPreciseAs(v, native, displayCode, 3)

  const rows = useMemo(
    () => buildBudgetRows({
      templates: data?.templates || [],
      spend: data?.spend || [],
      budgets,
      pool,
      cycle: plan?.cycle,
      // The first days of a cycle project one big upload into a month of the
      // same: no "At risk" from that (an older backend sends no flag).
      projectionReliable: plan?.usage?.projection_reliable !== false,
    }),
    [data?.templates, data?.spend, budgets, pool, plan?.cycle, plan?.usage?.projection_reliable],
  )
  const names = useMemo(() => new Map(rows.map(r => [r.id, r.name])), [rows])
  const summary = useMemo(() => summariseAllocation(budgets, pool, plan), [budgets, pool, plan])
  const untracked = useMemo(() => untrackedSpend(data?.spend || [], pool), [data?.spend, pool])

  const budgeted = rows.filter(r => r.amount > 0)
  const spentBudgeted = budgeted.reduce((sum, r) => sum + r.spent, 0)
  const projectedBudgeted = budgeted.reduce((sum, r) => sum + r.projected, 0)
  const needsAttention = rows.filter(r => r.status.rank >= STATUS.watch.rank).length

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    const test = (FILTERS.find(f => f.value === filter) || FILTERS[0]).test
    return rows
      .filter(test)
      .filter(r => !q || r.name.toLowerCase().includes(q))
      .sort(SORTS[sort] || SORTS.status)
  }, [rows, query, filter, sort])

  // Keep an open editor pointed at the live row, so a save from another tab
  // (or the spend finishing loading) shows up in it.
  const editingRow = editing ? rows.find(r => r.id === editing) || null : null

  const segments = useMemo(() => budgeted
    .slice()
    .sort((a, b) => b.amount - a.amount)
    .map((r, i) => ({ id: r.id, label: r.name, amount: r.amount, color: segmentColor(chart, i) })),
  [budgeted, chart])

  if (!pool) {
    return (
      <div className="p-5">
        <EmptyState
          icon={PiggyBank}
          title="This plan has no fixed fee to budget against"
          body="Template budgets are shares of your monthly plan fee. Once a plan with a fee is in place, you can split it between templates here."
        />
      </div>
    )
  }

  const acknowledged = summary.overPlan > 0 && overPlanAcknowledged !== null
    && Math.abs(overPlanAcknowledged - summary.allocated) < 0.5

  const save = (row, { amount, alertAt }) => {
    setEditing(null)
    if (amount <= 0) {
      // Emptying the field is a removal, and gets the same undo as one.
      if (budgets[row.id]) budgetsApi.removeBudget(row.id, row.name)
      return
    }
    const after = summariseAllocation({ ...budgets, [row.id]: { amount } }, pool)
    budgetsApi.setBudget(row.id, { amount, alertAt })
    if (after.overPlan > summary.overPlan) {
      onWarn?.('Budget saved', `Budgets are now ${format(after.overPlan)} over your ${pool.planName} plan.`)
    } else {
      onDone?.(`Budget saved for ${row.name}`)
    }
  }

  const exportCsv = () => {
    downloadCsv(
      `xtract-template-budgets-${new Date().toISOString().slice(0, 10)}.csv`,
      // Same encoder as the reports: BOM for Excel, and template names that
      // start with = + - @ are defused instead of running as a formula.
      encodeRows(buildBudgetCsvRows(rows.filter(r => r.amount > 0 || r.spent > 0), pool, summary)),
    )
    onDone?.('Budget report downloaded')
  }

  const spendLoading = data?.spendLoading && !data?.spend
  const busyRows = rows.filter(r => r.projected > 0 && !r.removed)

  return (
    <div className="p-5 space-y-5">
      {/* ================= the pool ================= */}
      <section
        data-tour="budget-pool"
        className="relative overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800
                   bg-gradient-to-br from-navy-50/70 via-white to-white
                   dark:from-blue-400/[0.07] dark:via-gray-900 dark:to-gray-900 p-5 animate-budget-rise"
      >
        <div aria-hidden="true" className="pointer-events-none absolute -top-24 -right-20 h-56 w-56 rounded-full
                                           bg-navy-200/30 dark:bg-blue-400/[0.06] blur-3xl" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.09em] text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
              <PiggyBank className="w-3.5 h-3.5" />
              Budget pool · {formatCycleLabel(plan?.cycle)}
              <HelpTip>
                Your monthly plan fee is the pool. Each template budget is a share of it, and a template’s spend is
                the pages it used this billing month at your plan’s rate of {formatPrecise(pool.planRate)} per page
                ({format(pool.fee)} ÷ {formatInt(pool.included)} included pages). Budgets that add up to the fee
                add up to your page allowance.
              </HelpTip>
            </p>
            <h2 className="mt-1.5 text-lg font-bold text-gray-900 dark:text-gray-100">
              {pool.planName} plan · {format(pool.fee)}
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap"> / month</span>
            </h2>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              {formatInt(pool.included)} pages included, then {formatPrecise(pool.overageRate)} per page
            </p>
          </div>
          <div className="flex items-center gap-2">
            {busyRows.length > 0 && (
              <button
                type="button"
                onClick={() => budgetsApi.distribute(rows, pool)}
                title="Share the whole plan fee between templates in proportion to their projected spend this billing month"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold
                           bg-white dark:bg-gray-800 text-navy-700 dark:text-navy-300
                           ring-1 ring-navy-200 dark:ring-navy-700 hover:bg-navy-50 dark:hover:bg-navy-800/40
                           transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
              >
                <Wand2 className="w-3.5 h-3.5" />
                Share plan by usage
              </button>
            )}
          </div>
        </div>

        <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
          <PoolTile
            label="Budgeted"
            delay={60}
            value={<CountUp value={summary.allocated} format={v => format(v)} />}
            foot={`${summary.count} ${summary.count === 1 ? 'template' : 'templates'} · ${Math.round(summary.usedPct)}% of plan`}
          />
          {summary.overPlan > 0 ? (
            <PoolTile
              label="Over plan"
              delay={110}
              tone="amber"
              value={<CountUp value={summary.overPlan} format={v => `+${format(v)}`} />}
              foot={`About ${formatInt(summary.extraPages)} pages of overage`}
            />
          ) : (
            <PoolTile
              label="Unallocated"
              delay={110}
              tone="emerald"
              value={<CountUp value={summary.unallocated} format={v => format(v)} />}
              foot={summary.count ? 'Still free to give a template' : 'The whole plan is unassigned'}
            />
          )}
          <PoolTile
            label="Spent by budgeted templates"
            delay={160}
            value={spendLoading ? <span className="skeleton inline-block h-6 w-20 align-middle" /> : <CountUp value={spentBudgeted} format={v => format(v)} />}
            foot={spendLoading ? 'Reading this billing month’s jobs…' : `Heading for ${format(projectedBudgeted)} by the end of the billing month`}
          />
          <PoolTile
            label="Need attention"
            delay={210}
            tone={needsAttention ? 'amber' : undefined}
            value={<CountUp value={needsAttention} />}
            foot={needsAttention ? 'Past alert, at risk or over' : 'Every budget is on track'}
            onClick={needsAttention ? () => setFilter('attention') : undefined}
          />
        </div>

        <div className="relative mt-5">
          <BudgetAllocationBar segments={segments} fee={pool.fee} format={format} />
        </div>
      </section>

      {/* ================= over plan ================= */}
      <div className="budget-reveal" data-open={summary.overPlan > 0 ? 'true' : 'false'} aria-live="polite">
        <div>
          {summary.overPlan > 0 && (
            acknowledged ? (
              <div className="flex items-center gap-2 flex-wrap rounded-xl border border-gray-200 dark:border-gray-800
                              bg-white dark:bg-gray-900 px-4 py-2.5 text-[12px] text-gray-600 dark:text-gray-300">
                <Check className="w-3.5 h-3.5 text-gray-400" />
                <span>
                  Budgets are {format(summary.overPlan)} over plan. You’ve chosen to keep this as overage
                  (about {formatInt(summary.extraPages)} pages).
                </span>
                <button
                  type="button"
                  onClick={() => budgetsApi.fit(pool)}
                  className="ml-auto font-medium text-navy-700 dark:text-navy-300 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded"
                >
                  Fit to plan instead
                </button>
              </div>
            ) : (
              <OverPlanCallout
                summary={summary}
                pool={pool}
                format={format}
                formatPrecise={formatPrecise}
                onFit={() => budgetsApi.fit(pool)}
                onCompare={onCompare}
                onAccept={() => budgetsApi.acknowledgeOverPlan(summary.allocated)}
              />
            )
          )}
        </div>
      </div>

      {/* ================= templates ================= */}
      <Card className="overflow-hidden">
        <div className="px-4 pt-4 pb-3 flex items-center gap-2 flex-wrap border-b border-gray-100 dark:border-gray-800">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Template budgets</h3>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              Spend this billing month, projected to {formatCycleEnd(plan?.cycle)}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <label className="relative">
              <span className="sr-only">Search templates</span>
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search templates"
                className="h-8 w-44 pl-8 pr-2.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700
                           bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 placeholder:text-gray-400
                           focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 transition-[width] focus:w-56"
              />
            </label>
            <select
              value={sort}
              onChange={e => setSort(e.target.value)}
              aria-label="Sort templates"
              className="h-8 pl-2.5 pr-7 text-xs rounded-lg border border-gray-200 dark:border-gray-700
                         bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
            >
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <button
              type="button"
              onClick={exportCsv}
              disabled={!rows.length}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium
                         border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200
                         hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
            >
              <Download className="w-3.5 h-3.5" />
              Export
            </button>
          </div>
        </div>

        <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 overflow-x-auto no-scrollbar">
          <Segmented
            ariaLabel="Filter templates"
            value={filter}
            onChange={setFilter}
            options={FILTERS.map(f => {
              const n = rows.filter(f.test).length
              return { value: f.value, label: `${f.label} ${n}` }
            })}
          />
        </div>

        {data?.spendError && (
          <p className="mx-4 mt-3 flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 px-3 py-2
                        text-[11.5px] text-amber-800 dark:text-amber-300">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            This billing month’s spend could not be read, so every template shows nothing spent. Budgets themselves are
            unaffected.
            <button type="button" onClick={data.reload} className="ml-auto font-semibold hover:underline">Retry</button>
          </p>
        )}

        <TemplateList
          loading={data?.templatesLoading && !data?.templates}
          error={data?.templatesError && !data?.templates}
          onRetry={data?.reload}
          rows={visible}
          totalRows={rows.length}
          maxSpent={Math.max(1, ...rows.map(r => r.spent))}
          spendLoading={spendLoading}
          progress={data?.spendProgress}
          format={format}
          onEdit={row => setEditing(row.id)}
        />

        <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800 text-[11px] text-gray-500 dark:text-gray-400 space-y-1">
          {untracked.spent > 0 && (
            <p>
              {format(untracked.spent)} ({formatInt(untracked.pages)} pages) this billing month wasn’t tied to a template
              (dynamic extraction, template set-up, or requests since deleted). It counts against the plan but not
              against any budget.
            </p>
          )}
          {data?.truncated && (
            <p className="text-amber-700 dark:text-amber-400">
              This billing month has more jobs than one read covers, so the oldest spend is missing and figures may be low.
            </p>
          )}
          <p className="flex items-center gap-1.5">
            Budgets are saved in this browser for your organisation and are a planning tool: nothing is blocked when
            one runs out.
            {budgetsApi.saveFailed && (
              <span className="text-rose-600 dark:text-rose-400 font-medium">The last change could not be saved here.</span>
            )}
          </p>
        </div>
      </Card>

      <BudgetEditor
        row={editingRow}
        budgets={budgets}
        names={names}
        pool={pool}
        plan={plan}
        chart={chart}
        displayCode={displayCode}
        format={format}
        formatPrecise={formatPrecise}
        onSave={save}
        onRemove={row => { budgetsApi.removeBudget(row.id, row.name); setEditing(null) }}
        onClose={() => setEditing(null)}
      />

      <UndoPill undo={budgetsApi.undo} onUndo={budgetsApi.undoLast} onDismiss={budgetsApi.dismissUndo} />
    </div>
  )
}

function formatCycleEnd(cycle) {
  if (!cycle?.end) return 'the end of the billing month'
  const end = new Date(new Date(cycle.end).getTime() - 1)
  if (Number.isNaN(end.getTime())) return 'the end of the billing month'
  return end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
}

// ---------------------------------------------------------------------------

const TILE_TONE = {
  rose: 'text-rose-600 dark:text-rose-400',
  emerald: 'text-emerald-600 dark:text-emerald-400',
  amber: 'text-amber-600 dark:text-amber-400',
}

function PoolTile({ label, value, foot, tone, delay = 0, onClick }) {
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`relative text-left rounded-lg bg-white/80 dark:bg-gray-900/60 ring-1 ring-gray-200/80 dark:ring-gray-800
                  px-3.5 py-3 animate-budget-rise backdrop-blur-sm
                  ${onClick ? 'hover:ring-amber-300 dark:hover:ring-amber-500/40 transition-shadow cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500' : ''}`}
      style={{ '--rise-delay': `${delay}ms` }}
    >
      <p className="text-[11px] text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`text-xl font-bold tabular-nums mt-0.5 tracking-tight ${TILE_TONE[tone] || 'text-gray-900 dark:text-gray-100'}`}>
        {value}
      </p>
      <p className="text-[10.5px] text-gray-400 dark:text-gray-500 mt-0.5 truncate">{foot}</p>
    </Tag>
  )
}

/**
 * The over-plan note. Informative, not an alarm: going over is a legitimate
 * choice (it is just overage), so it states the excess, what it would buy,
 * and the ways to resolve it, in the page's normal card styling.
 */
function OverPlanCallout({ summary, pool, format, formatPrecise, onFit, onCompare, onAccept }) {
  const pctDown = Math.round((1 - summary.fitScale) * 100)
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 border-l-[3px] border-l-amber-400
                    dark:border-l-amber-500 bg-white dark:bg-gray-900 px-4 py-3.5">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500" />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-gray-900 dark:text-gray-100">
            Budgets are {format(summary.overPlan)} over your {pool.planName} plan
          </p>
          <p className="mt-1 text-[12px] leading-relaxed text-gray-500 dark:text-gray-400">
            You’ve budgeted {format(summary.allocated)} against a {format(pool.fee)} plan. The extra{' '}
            {format(summary.overPlan)} is about {formatInt(summary.extraPages)} pages beyond your allowance, which
            would be billed as overage at {formatPrecise(pool.overageRate)} each ({format(summary.extraPagesCost, { pence: true })}).
            {summary.covers && <> The {summary.covers.name} plan ({format(summary.covers.fee)}/month) would cover it.</>}
          </p>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={onFit}
              title={pctDown > 0 ? `Makes every budget ${pctDown}% smaller` : 'Trims every budget to fit'}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold text-white
                         bg-navy-700 hover:bg-navy-800 dark:bg-navy-600 dark:hover:bg-navy-500 transition-colors
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-navy-500
                         dark:focus-visible:ring-offset-gray-900"
            >
              Fit to plan
            </button>
            <button
              type="button"
              onClick={onCompare}
              className="inline-flex items-center gap-1 h-8 px-3 rounded-lg text-xs font-medium
                         text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700
                         hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
            >
              Compare plans
              <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={onAccept}
              className="h-8 px-2 rounded-lg text-xs font-medium text-gray-500 dark:text-gray-400
                         hover:text-gray-800 dark:hover:text-gray-200 transition-colors
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
            >
              Keep as is
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------

function TemplateList({ loading, error, onRetry, rows, totalRows, maxSpent, spendLoading, progress, format, onEdit }) {
  if (loading) {
    return (
      <div className="p-4 space-y-2">
        {[0, 1, 2, 3].map(i => <div key={i} className="skeleton h-14 rounded-lg" />)}
        <span className="sr-only" role="status">Loading templates</span>
      </div>
    )
  }
  if (error) {
    return (
      <div className="py-2">
        <EmptyState icon={AlertCircle} title="Templates could not be loaded" body="Budgets are kept, but the template list did not come back." />
        <div className="flex justify-center pb-4">
          <button type="button" onClick={onRetry} className="text-xs font-semibold text-navy-700 dark:text-navy-300 hover:underline">Try again</button>
        </div>
      </div>
    )
  }
  if (!totalRows) {
    return <EmptyState icon={Layers} title="No templates yet" body="Create a template and it appears here, ready for a budget." />
  }
  if (!rows.length) {
    return <EmptyState icon={Search} title="Nothing matches" body="Try another filter or search." />
  }

  return (
    <>
      {spendLoading && (
        <p className="px-4 pt-3 text-[11px] text-gray-500 dark:text-gray-400" role="status">
          Reading this billing month’s spend{progress?.loaded ? ` · ${formatInt(progress.loaded)} ${progress.phase === 'jobs' ? 'jobs' : 'requests'} so far` : '…'}
        </p>
      )}
      <ul className="divide-y divide-gray-100 dark:divide-gray-800/70">
        {rows.map((row, i) => (
          <BudgetRow
            key={row.id}
            row={row}
            index={i}
            maxSpent={maxSpent}
            spendLoading={spendLoading}
            format={format}
            onEdit={() => onEdit(row)}
          />
        ))}
      </ul>
    </>
  )
}

function BudgetRow({ row, index, maxSpent, spendLoading, format, onEdit }) {
  const style = STATUS_STYLE[row.status.key]
  const hasBudget = row.amount > 0

  return (
    <li
      className="group grid grid-cols-1 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1.5fr)_8.75rem] gap-x-5 gap-y-2.5
                 items-center px-4 py-3 hover:bg-gray-50/80 dark:hover:bg-gray-800/30 transition-colors animate-budget-rise"
      style={{ '--rise-delay': `${Math.min(index, 12) * 30}ms` }}
    >
      {/* name */}
      <div className="min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-[13px] font-semibold text-gray-900 dark:text-gray-100 truncate">{row.name}</p>
          <span className={`inline-flex items-center gap-1.5 flex-shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${style.chip}`}>
            <span className="relative flex h-1.5 w-1.5">
              <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${style.dot}`} />
            </span>
            {row.status.label}
          </span>
        </div>
        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 truncate">
          {formatInt(row.requests)} {row.requests === 1 ? 'request' : 'requests'} · {formatInt(row.pages)} pages
          {row.removed && ' · no longer in your templates'}
          {row.inactive && ' · inactive'}
        </p>
      </div>

      {/* meter */}
      <div className="min-w-0">
        {spendLoading ? (
          <div className="skeleton h-2 rounded-full" />
        ) : hasBudget ? (
          <RowMeter row={row} barClass={style.bar} />
        ) : (
          <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
            <div
              className="h-full rounded-full bg-gray-300 dark:bg-gray-600 animate-grow-x"
              style={{ width: `${(row.spent / maxSpent) * 100}%`, animationDelay: `${index * 30}ms` }}
            />
          </div>
        )}
        <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px] tabular-nums">
          <span className="text-gray-600 dark:text-gray-300">
            <span className="font-semibold text-gray-900 dark:text-gray-100">{format(row.spent)}</span>
            {hasBudget ? <> of {format(row.amount)}</> : ' spent'}
          </span>
          <span className={row.status.key === 'risk' || row.status.key === 'over'
            ? 'text-gray-700 dark:text-gray-200 font-medium'
            : 'text-gray-400 dark:text-gray-500'}>
            {hasBudget
              ? row.remaining >= 0
                ? `Projected ${format(row.projected)}`
                : `${format(-row.remaining)} over`
              : row.projected > 0 ? `Projected ${format(row.projected)}` : 'No spend yet'}
          </span>
        </div>
      </div>

      {/* action */}
      <div className="flex md:justify-end">
        <button
          type="button"
          onClick={onEdit}
          aria-label={hasBudget ? `Edit budget for ${row.name}` : `Set a budget for ${row.name}`}
          className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold whitespace-nowrap
                      transition-all duration-200 group-hover:shadow-sm
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500
                      ${hasBudget
                        ? 'bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700 text-gray-800 dark:text-gray-100 hover:ring-navy-300 dark:hover:ring-navy-600'
                        : 'text-navy-700 dark:text-navy-300 border border-dashed border-navy-300 dark:border-navy-600 hover:bg-navy-50 dark:hover:bg-navy-800/40 hover:border-solid'}`}
        >
          {hasBudget ? (
            <>
              <span className="tabular-nums">{format(row.amount)}</span>
              <span className="text-gray-400 dark:text-gray-500 font-normal">/ mo</span>
            </>
          ) : (
            <>
              <Plus className="w-3.5 h-3.5" />
              Set budget
            </>
          )}
        </button>
      </div>
    </li>
  )
}

/**
 * Spent, projected and the alert line, against the budget. Scaled to the
 * larger of the budget and where the template is heading, so running over
 * shows *how far* over rather than a bar pinned at 100%.
 */
function RowMeter({ row, barClass }) {
  const reduced = usePrefersReducedMotion()
  const [mounted, setMounted] = useState(reduced)
  useEffect(() => {
    if (reduced) return
    const id = requestAnimationFrame(() => setMounted(true))
    return () => cancelAnimationFrame(id)
  }, [reduced])

  const scale = Math.max(row.amount, row.projected, row.spent, 1)
  const p = v => (mounted ? Math.min(100, (v / scale) * 100) : 0)
  const within = Math.min(row.spent, row.amount)
  const beyond = Math.max(0, row.spent - row.amount)
  const tail = Math.max(0, row.projected - row.spent)

  return (
    <div
      className="relative h-2 rounded-full bg-gray-100 dark:bg-gray-800"
      role="meter"
      aria-valuemin={0}
      aria-valuemax={row.amount}
      aria-valuenow={Math.round(row.spent)}
      aria-label={`${row.name}: ${Math.round(row.pctSpent || 0)}% of budget spent`}
    >
      <div className="absolute inset-0 rounded-full overflow-hidden">
        <div className={`absolute inset-y-0 left-0 ${barClass} transition-[width] duration-[900ms] ease-out`} style={{ width: `${p(within)}%` }} />
        {beyond > 0 && (
          <div className="absolute inset-y-0 bg-rose-500 dark:bg-rose-400 transition-[left,width] duration-[900ms] ease-out"
               style={{ left: `${p(row.amount)}%`, width: `${p(beyond)}%` }} />
        )}
        {tail > 0 && (
          <div className={`absolute inset-y-0 bg-gray-300 dark:bg-gray-600
                           transition-[left,width] duration-[900ms] delay-150 ease-out`}
               style={{ left: `${p(row.spent)}%`, width: `${p(tail)}%` }} />
        )}
      </div>
      {/* alert threshold */}
      <span
        className="absolute -top-0.5 -bottom-0.5 w-px bg-amber-500/80 transition-[left] duration-700"
        style={{ left: `${p(row.amount * (row.alertAt / 100))}%` }}
        title={`Alert at ${row.alertAt}%`}
      />
      {/* the budget itself, once something runs past it */}
      {scale > row.amount && (
        <span className="absolute -top-1 -bottom-1 w-0.5 rounded-full bg-gray-900 dark:bg-gray-100 transition-[left] duration-700"
              style={{ left: `calc(${p(row.amount)}% - 1px)` }} title="Budget" />
      )}
    </div>
  )
}

/** One step of undo, floating at the bottom of the screen for a few seconds. */
function UndoPill({ undo, onUndo, onDismiss }) {
  useEffect(() => {
    if (!undo) return
    const id = setTimeout(onDismiss, 8000)
    return () => clearTimeout(id)
  }, [undo, onDismiss])

  if (!undo) return null
  return createPortal(
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60]">
      <div role="status" className="flex items-center gap-3 rounded-full bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900
                                    pl-4 pr-1.5 py-1.5 shadow-2xl animate-budget-rise text-[13px]">
        <span>{undo.label}</span>
        <button
          type="button"
          onClick={onUndo}
          className="inline-flex items-center gap-1 rounded-full bg-white/15 dark:bg-gray-900/10 px-3 py-1 font-semibold
                     hover:bg-white/25 dark:hover:bg-gray-900/20 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <Undo2 className="w-3.5 h-3.5" />
          Undo
        </button>
        <button type="button" onClick={onDismiss} aria-label="Dismiss" className="p-1 rounded-full opacity-60 hover:opacity-100">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>,
    document.body,
  )
}
